import { LitheSpecificElement } from '@wheelbox/lithejs-engine';
import { compileMarkdown } from '@wheelbox/lithejs-markdown';

export interface DocMeta {
  filename: string;
  title: string;
  order: number;
}

export interface DocData {
  title: string;
  keywords: string[];
  content: LitheSpecificElement;
}

/**
 * Parse frontmatter from markdown content
 */
function parseFrontmatter(content: string): {
  frontmatter: Record<string, string | string[]>;
  body: string;
} {
  const match = content.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);

  if (!match) {
    return { frontmatter: {}, body: content };
  }

  const [, frontmatterRaw, body] = match;
  const frontmatter: Record<string, string | string[]> = {};

  frontmatterRaw.split('\n').forEach((line) => {
    const colonIndex = line.indexOf(':');
    if (colonIndex > -1) {
      const key = line.substring(0, colonIndex).trim();
      let value = line.substring(colonIndex + 1).trim();

      // Handle arrays like keywords: ["a", "b"]
      if (value.startsWith('[') && value.endsWith(']')) {
        try {
          frontmatter[key] = JSON.parse(value);
        } catch {
          frontmatter[key] = value;
        }
      } else {
        // Remove quotes if present
        if (
          (value.startsWith('"') && value.endsWith('"')) ||
          (value.startsWith("'") && value.endsWith("'"))
        ) {
          value = value.slice(1, -1);
        }
        frontmatter[key] = value;
      }
    }
  });

  return { frontmatter, body };
}

/**
 * Fetch docs index (list of all documentation sections) with localized titles
 */
export async function fetchDocsIndex(
  language: string = 'en'
): Promise<DocMeta[]> {
  const response = await fetch('/docs/index.json');
  if (!response.ok) {
    throw new Error(`Failed to fetch docs index: ${response.statusText}`);
  }
  const index: DocMeta[] = await response.json();

  // Fetch localized titles from markdown files
  const localizedIndex = await Promise.all(
    index.map(async (item) => {
      try {
        const mdResponse = await fetch(`/docs/${item.filename}_${language}.md`);
        if (mdResponse.ok) {
          const mdContent = await mdResponse.text();
          const { frontmatter } = parseFrontmatter(mdContent);
          return {
            ...item,
            title: (frontmatter.title as string) || item.title,
          };
        }
      } catch {
        // Fall back to default title
      }
      return item;
    })
  );

  return localizedIndex;
}

/**
 * Fetch a single documentation page
 */
export async function fetchDoc(
  filename: string,
  language: string = 'en'
): Promise<DocData> {
  const response = await fetch(`/docs/${filename}_${language}.md`);

  if (!response.ok) {
    throw new Error(`Failed to fetch doc: ${response.statusText}`);
  }

  const rawContent = await response.text();
  const { frontmatter, body } = parseFrontmatter(rawContent);

  return {
    title: (frontmatter.title as string) || filename,
    keywords: (frontmatter.keywords as string[]) || [],
    content: compileMarkdown(body) as LitheSpecificElement,
  };
}
